import { AvatarFace, AvatarImage } from '@forecastify/ui';
import { identityColor, initialsOf } from './identity';
import { useAvatar } from './use-avatar';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 28 Oct 2026 · one person's face beside their name, with their PHOTO.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The leave drawer and the contract, invoice and cost rails drew `AvatarFace` alone — which can
 * only draw the generated face — so somebody with an uploaded picture showed their Blobatar there
 * and their photo everywhere else (UAT, 28 Oct 2026). This is the list cells' own stack in one
 * place: the identity colour, the generated face (or initials) on it, and the photo on top, so a
 * picture that fails to load reveals the face rather than a blank disc.
 *
 * ⚠️ The photo comes through `useAvatar`, the app's one avatar cache: a path fetched once, however
 * many rows draw it. Decorative — the name beside it is the accessible text.
 */
export function PersonFace({
  name,
  seed,
  url,
  colorKey,
  size = 20,
}: {
  readonly name: string;
  readonly seed: string | null | undefined;
  /** The picture's path (`/files/avatar/<user>`), or null when they have none. */
  readonly url: string | null | undefined;
  /** What the disc's colour is derived from — the member id where known, else the name. */
  readonly colorKey?: string | null;
  readonly size?: number;
}) {
  const photo = useAvatar(url);
  return (
    <span
      aria-hidden
      className="text-on-id relative grid shrink-0 place-items-center overflow-hidden rounded-full text-[9px] font-semibold"
      style={{ width: size, height: size, background: identityColor(colorKey ?? name) }}
    >
      <AvatarFace seed={seed ?? undefined} fallback={initialsOf(name)} size={size} />
      <AvatarImage src={photo} />
    </span>
  );
}
